// The whole code is copied from VPC.

/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import * as _ from 'underscore';
import { DataGrid } from '@tableau/widgets-datagrid';
import { AppUrls } from '../../../app/AppUrls';
import { ContentDefinitions } from '../../models/contentDefinitions';
import { ContentType } from '../../models/ContentType';
import { SearchResultItem } from '../../models/SearchResultTypes';
import { IOrderClause } from '../../models/sort/IOrderClause';
import { ColumnFactoryMethod, CommonColumns, ListViewGridColumnGenerator } from './ListViewGridColumnGenerator';
import { ContentTypeFilterAllOption } from '../../models/ContentTypeFilterValue';
import { FeatureFlag, FeatureFlagService } from '../../services/FeatureFlagService';

export interface ListViewGridContentGeneratorBaseParams {
  readonly items: ReadonlyArray<SearchResultItem>;
  readonly contentType: ContentType;
  readonly requestContentSlice: (offset: number, count: number, contentType: ContentType) => void;
  readonly contentFilter: ReadonlyArray<any>;
  readonly currentSort: ReadonlyArray<IOrderClause>;
  readonly changeSort: (field: string) => void;
  readonly setSortOrder: (order: ReadonlyArray<IOrderClause>) => void;
  readonly isSessionSiteOrServerAdmin: boolean;
  readonly displayScheduleDescriptionAsName: boolean;
  readonly inServerArea: boolean;
  readonly isGuestEnabled: boolean;
  readonly urls: AppUrls;
  readonly searchTerm: string;
  readonly totalForMixedContent: number;
  readonly selectedContentType: string;
}

export type ListViewGridContentGeneratorParams = ListViewGridContentGeneratorBaseParams;

export class ListViewGridContentGenerator {
  public static getItemDebounceDuration = 400;

  private columnGenerator: ListViewGridColumnGenerator | undefined;

  private getItemsDebounceTimer: any;

  private params: ListViewGridContentGeneratorParams;
  private rows: DataGrid.Row[] = [];

  public cols: DataGrid.Column[] = [];
  public rowStore = (): DataGrid.RowStore => {
    return {
      getItems: (start: number, end: number, rowHandler: (rows: DataGrid.Row[]) => void) => {
        rowHandler(this.rows);

        // requestContentSlice fetches content from start to end, including start and excluding end
        // rowStore is defined as "the last row that will be rendered per the current viewport" so it is inclusive.
        // We need to add one to end to make sure we fetch all of the data.
        clearTimeout(this.getItemsDebounceTimer);
        this.getItemsDebounceTimer = setTimeout(() => {
          this.params.requestContentSlice(start, end + 1 - start, this.params.contentType);
        }, ListViewGridContentGenerator.getItemDebounceDuration);
      },
    };
  };

  public constructor(params: ListViewGridContentGeneratorParams) {
    this.params = params;
    this.initializeRowsAndColumns();
  }

  public generateContent(params: ListViewGridContentGeneratorParams): void {
    // re initialize columns if one of the below properties changed
    const reInitializeColumns: boolean = !_.isEmpty(
      _.omit(
        _.pick(this.params, 'contentFilter', 'inServerArea', 'ignoreServerExtractPriority', 'isGuestEnabled', 'currentSort'),
        function (value: any, key: any) {
          return (params as any)[key] === value;
        }
      )
    );

    this.params = params;

    if (reInitializeColumns) {
      this.initializeRowsAndColumns();
    } else {
      // here we don't want to regenerate columns but we do need to reset the items and update
      // column generator with updated props
      this.setRowDataFromItems();
      const { items, requestContentSlice, ...columnGeneratorParams } = this.params;
      this.columnGenerator!.updateParams({
        ...columnGeneratorParams,
        displayingItems: items.length > 0,
      });
    }
  }

  private setRowDataFromItems() {
    // Row data gets reset when props are updated
    this.rows = new Array(this.params.items.length);

    this.params.items.forEach((item: SearchResultItem, index: number) => {
      if (item) {
        this.rows[index] = {
          key: item.id,
          data: { ...item, rowIndex: index }, // this has to be rowIndex instead of index so it doesn't override the 'index' in data TFSID944970
        };
      }
    });
  }

  private initializeRowsAndColumns(): void {
    this.setRowDataFromItems();

    /*
     * Guidance toward future refactoring (DRBR, 2019-03-18):
     *
     * My goal is to easily add/define columns for a new content type / subplace. I want those columns
     * to be defined in the module that my team owns (the unique columns, at least). I also want a clear
     * place to list the columns for explorer grids displaying my content type across all the various
     * pages where that grid might appear (preferably, my module would own that listing as well).
     * It seems natural to have the column list be part of the ContentDefinition object for each
     * content type. If the ContentGenerator can reference the column listing from there,
     * the ContentDefinition could easily also point to a list defined in a different module.
     *
     * An idea to make this happen is to pivot this code so that each column list lives in a
     * `listViewColumnTypes(spaceInfo) function on its content type's definition object (in
     * ContentDefinitions.ts). The ContentGenerator would fetch the content definition for whichever
     * content type we're currently viewing via some helper code in SpaceInfo.ts (there would still
     * need to be a special case for mixed content), then pass the space info into the
     * `listViewColumnTypes` function, which would return the correct columns.
     *
     * In addition, columns can now be defined as standalone objects or factory functions, for cases
     * where their implementations are sufficiently unique enough to warrant not living in the
     * ColumnGenerator file itself. We can pull the column definitions out from the giant switch
     * statement, as appropriate, by changing each one into a function that takes a subset of the
     * ColumnGenerator params, or just a static column object. Then, as long as their params are a
     * subset of the ones provided by the ColumnGenerator, special columns can be defined in any module.
     */

    // Filtering on one specific type, simply returning that list of columns for that contenttype
    // Otherwise return the mixed content type columns
    // This assumes the content types are unique in contentFilter

    const columnFactoryMethods: ColumnFactoryMethod[] = [...(ContentDefinitions[this.params.contentType].listViewColumns || [])];
    if (FeatureFlagService.instance.getValue(FeatureFlag.offspringPostMVP224)) {
      columnFactoryMethods.splice(1, 0, CommonColumns.favorite);
    }

    const { items, requestContentSlice: requestContentSlice, ...columnGeneratorParams } = this.params;
    this.columnGenerator = new ListViewGridColumnGenerator({
      ...columnGeneratorParams,
      displayingItems: items.length > 0,
      searchTerm: this.params.searchTerm,
      total: this.params.selectedContentType === ContentTypeFilterAllOption ? this.params.totalForMixedContent : this.params.items.length,
    });

    this.cols = _.compact(columnFactoryMethods.map((columnFactoryMethod) => this.columnGenerator!.getColumn(columnFactoryMethod)));
  }
}
